import { cn } from "~/common/cn"
import { avatarColorClass } from "~/common/avatar-color"
import { AppText } from "./app-text"
import circleWavyCheck from "~/images/circle-wavy-check"

type UserAvatarSize = "xsm" | "xsm-sm" | "sm" | "md" | "lg" | "shareCard"

interface UserAvatarProps {
  avatarUrl?: string | null
  displayName: string
  size?: UserAvatarSize
  className?: string
  imgClassName?: string
  textClassName?: string
  verifiedAccount?: boolean
}

const VerifiedBadge = ({ size }: { size: number }): JSX.Element => (
  <circleWavyCheck.Icon
    borderColor={"#FCFFFA33"}
    borderWidth={1}
    fill="#2D92FF"
    width={size}
    height={size}
    className="absolute bottom-[-2px] right-[-2px]"
  />
)

export const BadgeSize: Record<UserAvatarSize, number> = {
  "xsm": 8,
  "xsm-sm": 10,
  "sm": 13,
  "md": 24,
  "lg": 32,
  "shareCard": 18,
}

export const UserAvatar = ({
  avatarUrl,
  displayName,
  verifiedAccount,
  size = "md",
  className,
  imgClassName,
  textClassName,
}: UserAvatarProps): JSX.Element => {
  const sizeClasses = {
    "xsm": "h-[20px] w-[20px]",
    "xsm-sm": "h-[28px] w-[28px]",
    "sm": "h-[36px] w-[36px]",
    "md": "h-[73px] w-[73px]",
    "lg": "h-[99px] w-[99px]",
    "shareCard": "w-[42px]",
  }

  const textSizeClasses = {
    "xsm": "text-[10px]",
    "xsm-sm": "text-[12px]",
    "sm": "text-[14px]",
    "md": "text-[29px]",
    "lg": "text-[40px]",
    "shareCard": "text-[24px]",
  }

  if (avatarUrl) {
    return (
      <div className="relative flex-shrink-0">
        <img
          src={avatarUrl}
          alt={displayName}
          className={cn(
            sizeClasses[size],
            "aspect-square rounded-full border-[1px] border-base-white/20 object-cover",
            imgClassName
          )}
          loading="eager"
        />
        {verifiedAccount && <VerifiedBadge size={BadgeSize[size]} />}
      </div>
    )
  }

  return (
    <div
      className={cn(
        "relative flex flex-row items-center justify-center gap-1 rounded-full border-[1px] border-base-white/20",
        sizeClasses[size],
        "aspect-square rounded-full",
        avatarColorClass(displayName),
        className
      )}
    >
      <AppText variant="h3" className={cn("opacity-70", textSizeClasses[size], textClassName)}>
        {displayName.charAt(0).toUpperCase()}
      </AppText>
      {verifiedAccount && <VerifiedBadge size={BadgeSize[size]} />}
    </div>
  )
}
